import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Heading,
  Text,
  Box,
  Flex,
  UnorderedList,
  ListItem,
  Button,
  Link as ChakraLink,
  OrderedList,
  Icon,
} from '@chakra-ui/react'
import { Image } from '../../HOC/Image'
import Arrow from '@/public/assets/icons/arrow-light.svg'
import { StaticImport } from 'next/dist/shared/lib/get-img-props'
import { GameRules } from 'types/types'
import { useState } from 'react'
import { useTranslation } from 'next-i18next/pages'

import Basket1 from '@/public/assets/images/basket/wizzard-1.png'
import Basket2 from '@/public/assets/images/basket/wizzard-2.png'
import Basket3 from '@/public/assets/images/basket/wizzard-3.png'
import Coinflip1 from '@/public/assets/images/coinflip/wizzard-1.png'
import Coinflip2 from '@/public/assets/images/coinflip/wizzard-2.png'
import Coinflip3 from '@/public/assets/images/coinflip/wizzard-3.png'
import Mines1 from '@/public/assets/images/mines/wizzard-1.png'
import Mines2 from '@/public/assets/images/mines/wizzard-2.png'
import Mines3 from '@/public/assets/images/mines/wizzard-3.png'
import Mines4 from '@/public/assets/images/mines/wizzard-4.png'
import Mines5 from '@/public/assets/images/mines/wizzard-5.png'
import Jackpot1 from '@/public/assets/images/jackpot/wizzard-1.png'
import Jackpot2 from '@/public/assets/images/jackpot/wizzard-2.png'
import Jackpot3 from '@/public/assets/images/jackpot/wizzard-3.png'
import Jackpot4 from '@/public/assets/images/jackpot/wizzard-4.png'
import Double1 from '@/public/assets/images/double/wizzard-1.png'
import Double2 from '@/public/assets/images/double/wizzard-2.png'
import Double3 from '@/public/assets/images/double/wizzard-3.png'
import Double4 from '@/public/assets/images/double/wizzard-4.png'
import Lootbox1 from '@/public/assets/images/lootbox/wizzard-1.png'
import Lootbox2 from '@/public/assets/images/lootbox/wizzard-2.png'
import Lootbox3 from '@/public/assets/images/lootbox/wizzard-3.png'
import Lootbox4 from '@/public/assets/images/lootbox/wizzard-4.png'
import Lootbox5 from '@/public/assets/images/lootbox/wizzard-5.png'
import Balance1 from '@/public/assets/images/balance/wizzard-1.png'
import Competition1 from '@/public/assets/images/leaderboard/wizzard-1-1.png'
import Competition2 from '@/public/assets/images/leaderboard/wizzard-2.png'
import Competition3 from '@/public/assets/images/leaderboard/wizzard-3.png'
import Competition4 from '@/public/assets/images/leaderboard/wizzard-4.png'
import Competition5 from '@/public/assets/images/leaderboard/wizzard-5.png'
import Competition6 from '@/public/assets/images/leaderboard/wizzard-6.png'

import Runes1 from '@/public/assets/images/runes/wizzard-1.png'
import Runes2 from '@/public/assets/images/runes/wizzard-2.png'
import Runes3 from '@/public/assets/images/runes/wizzard-3.png'
import Runes4 from '@/public/assets/images/runes/wizzard-4.png'

import { AnimatePresence, motion, wrap } from 'framer-motion'

export default ({ onClose, game }: { onClose: () => void; game: GameRules }) => {
  return (
    <Modal size={'lg'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalCloseButton onClick={onClose} size="sm" />
        <ModalBody pb={5} pt={10}>
          {game === GameRules.JACKPOT && <JackpotRules />}
          {game === GameRules.DOUBLE && <DoubleRules />}
          {game === GameRules.LOOTBOX && <LootboxRules />}
          {game === GameRules.MINES && <MinesRules />}
          {game === GameRules.BASKET && <AuctionBasketRules />}
          {game === GameRules.OPENING_COMPETITION && <RunesCompetitionRules onClose={onClose} />}
          {game === GameRules.COINFLIP && <CoinflipRules />}
          {game === GameRules.BONUS_FUNDS && <BonusFunds />}
          {game === GameRules.RUNES && <RunesRules />}
          {game === GameRules.RUNES_COMPETITION && <RunesCompetitionRules onClose={onClose} />}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const variants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }
  },
}

const swipeConfidenceThreshold = 10000
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity
}

const RulesWizzard = ({ steps, onClose }: { steps: Array<Step>; onClose?: () => void }) => {
  const [[page, direction], setPage] = useState([0, 0])
  const imageIndex = wrap(0, steps.length, page)
  const currentStep = steps[imageIndex]

  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection])
  }

  return (
    <>
      <Box pos={'relative'} overflowX={'hidden'} pb={4}>
        <AnimatePresence initial={false} custom={0}>
          <motion.div
            style={{ position: 'absolute' }}
            custom={direction}
            key={page}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(_, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x)

              if (swipe < -swipeConfidenceThreshold && imageIndex !== steps.length - 1) {
                paginate(1)
              } else if (swipe > swipeConfidenceThreshold && imageIndex !== 0) {
                paginate(-1)
              }
            }}
          >
            <Box pos={'relative'}>
              {steps[imageIndex].video && (
                <Box
                  as="video"
                  loop
                  src={steps[imageIndex].video!}
                  objectFit="cover"
                  width="464px"
                  autoPlay
                  height="100%"
                  rounded={'2xl'}
                  style={{
                    position: 'absolute',
                    height: '100%',
                  }}
                />
              )}
              <Image rounded={'2xl'} src={steps[imageIndex].graphic} alt="" />
            </Box>
          </motion.div>
        </AnimatePresence>
        <Box opacity={0} pos={'relative'}>
          {steps.map((e, index) => (
            <Box key={index} position={'absolute'}>
              <Image src={e.graphic} alt="" />
            </Box>
          ))}
          <Image src={steps[imageIndex].graphic} alt="" />
        </Box>
        {steps.length > 1 && <Controller setStep={paginate} step={imageIndex} stepsLength={steps.length} />}
      </Box>

      <Heading userSelect={'none'} fontSize={'32px'} mb={2}>
        {currentStep.title}
      </Heading>
      {currentStep.content}

      {steps.length > 1 && (
        <Flex mt={6} gap={1} justifyContent={'center'}>
          {steps.map((_, index) => (
            <Box
              key={index}
              w={2}
              h={2}
              rounded={'full'}
              bg={imageIndex === index ? 'linear-gradient(292deg, #8E83FB 31.11%, #5D52EE 97.01%)' : 'ninja.400'}
            />
          ))}
        </Flex>
      )}
      {currentStep.cta && <Box mt={4}>{currentStep.cta(onClose)}</Box>}
    </>
  )
}

const Controller = ({
  step,
  setStep,
  stepsLength,
}: {
  step: number
  setStep: (newDirection: number) => void
  stepsLength: number
}) => {
  const [isFirst, isLast] = [step === 0, step === stepsLength - 1]

  const handleNextStep = () => {
    if (step === 0) {
      return
    }

    setStep(-1)
  }

  const handlePrevStep = () => {
    if (step === stepsLength - 1) {
      return
    }

    setStep(1)
  }

  return (
    <Flex gap={1} pos={'absolute'} zIndex={10} right={4} bottom={0}>
      <Flex
        w={8}
        h={8}
        rounded={'full'}
        justifyContent={'center'}
        alignItems={'center'}
        transform={'rotate(180deg)'}
        bg={'ninja.100'}
        onClick={handleNextStep}
        color={'ninja.500'}
        cursor={'pointer'}
      >
        <Box opacity={isFirst ? 0.4 : 1}>
          <Icon w={3} h={3} as={Arrow} />
        </Box>
      </Flex>
      <Flex
        w={8}
        h={8}
        rounded={'full'}
        justifyContent={'center'}
        alignItems={'center'}
        bg={'ninja.100'}
        onClick={handlePrevStep}
        color={'ninja.500'}
        cursor={'pointer'}
      >
        <Box opacity={isLast ? 0.4 : 1}>
          <Icon w={3} h={3} as={Arrow} />
        </Box>
      </Flex>
    </Flex>
  )
}

const BonusFunds = () => {
  const { t } = useTranslation()
  return <RulesWizzard steps={getBonusFundsSteps(t)} />
}

const MinesRules = () => {
  const { t } = useTranslation()
  return <RulesWizzard steps={getMinesSteps(t)} />
}

const RunesRules = () => {
  const { t } = useTranslation()
  return <RulesWizzard steps={getRunesSteps(t)} />
}

const OpeningCompetitionRules = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation()
  return <RulesWizzard steps={getOpeningCompetitionSteps(t)} onClose={onClose} />
}

const RunesCompetitionRules = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation()
  return <RulesWizzard steps={getRunesCompetitionSteps(t)} onClose={onClose} />
}

const JackpotRules = () => {
  const { t } = useTranslation()
  return <RulesWizzard steps={getJackpotSteps(t)} />
}

const DoubleRules = () => {
  const { t } = useTranslation()
  return <RulesWizzard steps={getDoubleSteps(t)} />
}

const LootboxRules = () => {
  const { t } = useTranslation()
  return <RulesWizzard steps={getLootboxSteps(t)} />
}

const AuctionBasketRules = () => {
  const { t } = useTranslation()
  return <RulesWizzard steps={getBasketSteps(t)} />
}

const CoinflipRules = () => {
  const { t } = useTranslation()
  return <RulesWizzard steps={getCoinflipSteps(t)} />
}

type Step = {
  title: string
  video?: string
  graphic: StaticImport
  content: React.ReactElement
  cta?: (onClose?: () => void) => React.ReactElement
}

type TFunc = (key: string, opts?: any) => string

const getBonusFundsSteps = (t: TFunc): Array<Step> => [
  {
    title: t('rules.bonus-funds.step1-title'),
    graphic: Balance1,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.bonus-funds.step1-body-before')}{' '}
        <Text
          fontWeight={600}
          color={'white'}
          bg={'#8B60FA'}
          bgBlendMode={'overlay'}
          bgGradient={`linear-gradient(90deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.25) 25%, rgba(255, 255, 255, 0.5) 33%, rgba(255, 255, 255, 0.25) 66%, rgba(255, 255, 255, 0.25) 75%, rgba(255, 255, 255, 0.25) 100%)`}
          backgroundClip={'text'}
          __css={{
            '-webkit-text-fill-color': 'transparent',
          }}
          as={'span'}
          display={'inline-block'}
        >
          {t('rules.bonus-funds.step1-body-purple')}
        </Text>{' '}
        {t('rules.bonus-funds.step1-body-after')}
      </Text>
    ),
  },
]

const getMinesSteps = (t: TFunc): Array<Step> => [
  {
    title: t('rules.mines.step1-title'),
    video: '/assets/videos/mines_rule.mp4',
    graphic: Mines1,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.mines.step1-body')}
      </Text>
    ),
  },
  {
    title: t('rules.mines.step2-title'),
    graphic: Mines2,
    content: (
      <>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={700} color={'ninja.100'} mb={1}>
          <Text as={'span'} fontWeight={500}>
            {t('rules.mines.step2-intro')}{' '}
          </Text>{' '}
          {t('rules.mines.step2-follow')}
        </Text>
        <OrderedList userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          <ListItem mb={1}>{t('rules.mines.step2-item1')}</ListItem>
          <ListItem mb={1}>{t('rules.mines.step2-item2')}</ListItem>
          <ListItem mb={1}>{t('rules.mines.step2-item3')}</ListItem>
          <ListItem>{t('rules.mines.step2-item4')}</ListItem>
        </OrderedList>
      </>
    ),
  },
  {
    title: t('rules.mines.step3-title'),
    graphic: Mines3,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.mines.step3-body')}
      </Text>
    ),
  },
  {
    title: t('rules.mines.step4-title'),
    graphic: Mines4,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.mines.step4-body')}
      </Text>
    ),
  },
  {
    title: t('rules.mines.step5-title'),
    graphic: Mines5,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.mines.step5-body')}
      </Text>
    ),
  },
]

const getRunesSteps = (t: TFunc): Array<Step> => [
  {
    title: t('rules.runes.step1-title'),
    graphic: Runes1,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.runes.step1-body')}
      </Text>
    ),
  },
  {
    title: t('rules.runes.step2-title'),
    graphic: Runes2,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.runes.step2-body')}
      </Text>
    ),
  },
  {
    title: t('rules.runes.step3-title'),
    graphic: Runes3,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.runes.step3-body')}
      </Text>
    ),
  },
  {
    title: t('rules.runes.step4-title'),
    graphic: Runes4,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.runes.step4-body')}
      </Text>
    ),
  },
]

const getBasketSteps = (t: TFunc): Array<Step> => [
  {
    title: t('rules.basket.step1-title'),
    graphic: Basket1,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.basket.step1-body')}
      </Text>
    ),
  },
  {
    title: t('rules.basket.step2-title'),
    graphic: Basket2,
    content: (
      <>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={700} color={'ninja.100'} mb={1}>
          {t('rules.basket.step2-intro')}
        </Text>
        <OrderedList userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          <ListItem mb={1}>
            <Text as={'b'}>{t('rules.basket.step2-item1-label')}</Text> - {t('rules.basket.step2-item1-body')}
          </ListItem>
          <ListItem mb={1}>
            <Text as={'b'}>{t('rules.basket.step2-item2-label')}</Text> - {t('rules.basket.step2-item2-body')}
          </ListItem>
          <ListItem mb={1}>
            <Text as={'b'}>{t('rules.basket.step2-item3-label')}</Text> - {t('rules.basket.step2-item3-body')}
          </ListItem>
          <ListItem>
            <Text as={'b'}>{t('rules.basket.step2-item4-label')}</Text> - {t('rules.basket.step2-item4-body')}
          </ListItem>
        </OrderedList>
      </>
    ),
  },
  {
    title: t('rules.basket.step3-title'),
    graphic: Basket3,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.basket.step3-body')}
      </Text>
    ),
  },
]

const getCoinflipSteps = (t: TFunc): Array<Step> => [
  {
    title: t('rules.coinflip.step1-title'),
    graphic: Coinflip1,
    content: (
      <>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'} mb={1}>
          {t('rules.coinflip.step1-body1')}
        </Text>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          {t('rules.coinflip.step1-body2')}
        </Text>
      </>
    ),
  },
  {
    title: t('rules.coinflip.step2-title'),
    graphic: Coinflip2,
    content: (
      <>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={700} color={'ninja.100'} mb={1}>
          {t('rules.coinflip.step2-intro')}
        </Text>
        <OrderedList userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          <ListItem mb={1}>
            <Text as={'b'}>{t('rules.coinflip.step2-item1-label')} </Text>
            {t('rules.coinflip.step2-item1-body')}
          </ListItem>
          <ListItem mb={1}>
            <Text as={'b'}>{t('rules.coinflip.step2-item2-label')} </Text> {t('rules.coinflip.step2-item2-body')}
          </ListItem>
        </OrderedList>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={700} color={'ninja.100'}>
          {t('rules.coinflip.step2-outro')} 🚀
        </Text>
      </>
    ),
  },
  {
    title: t('rules.coinflip.step3-title'),
    graphic: Coinflip3,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.coinflip.step3-body')}
      </Text>
    ),
  },
]

const getJackpotSteps = (t: TFunc): Array<Step> => [
  {
    title: t('rules.jackpot.step1-title'),
    graphic: Jackpot1,
    video: '/assets/videos/jackpot_rule.mp4',
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.jackpot.step1-body')}
      </Text>
    ),
  },
  {
    title: t('rules.jackpot.step2-title'),
    graphic: Jackpot2,
    content: (
      <UnorderedList userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        <ListItem mb={1}>{t('rules.jackpot.step2-item1')}</ListItem>
        <ListItem mb={1}>{t('rules.jackpot.step2-item2')}</ListItem>
        <ListItem mb={1}>{t('rules.jackpot.step2-item3')}</ListItem>
      </UnorderedList>
    ),
  },
  {
    title: t('rules.jackpot.step3-title'),
    graphic: Jackpot3,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.jackpot.step3-body')}
      </Text>
    ),
  },
  {
    title: t('rules.jackpot.step4-title'),
    graphic: Jackpot4,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.jackpot.step4-body')}
      </Text>
    ),
  },
]

const getLootboxSteps = (t: TFunc): Array<Step> => [
  {
    title: t('rules.lootbox.step1-title'),
    graphic: Lootbox1,
    video: '/assets/videos/lootbox_rule.mp4',
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.lootbox.step1-body')}
      </Text>
    ),
  },
  {
    title: t('rules.lootbox.step2-title'),
    graphic: Lootbox2,
    content: (
      <>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          {t('rules.lootbox.step2-intro')}
        </Text>
        <UnorderedList userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          <ListItem mb={1}>
            <Text as={'span'} fontWeight={700}>
              {t('rules.lootbox.step2-item1-label')}
            </Text>
            {t('rules.lootbox.step2-item1-body')}
          </ListItem>
          <ListItem mb={1}>
            <Text as={'span'} fontWeight={700}>
              {t('rules.lootbox.step2-item2-label')}
            </Text>
            {t('rules.lootbox.step2-item2-body')}
          </ListItem>
          <ListItem mb={1}>
            <Text as={'span'} fontWeight={700}>
              {t('rules.lootbox.step2-item3-label')}
            </Text>
            {t('rules.lootbox.step2-item3-body')}
          </ListItem>
        </UnorderedList>
      </>
    ),
  },
  {
    title: t('rules.lootbox.step3-title'),
    graphic: Lootbox3,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.lootbox.step3-body')}
      </Text>
    ),
  },
  {
    title: t('rules.lootbox.step4-title'),
    graphic: Lootbox4,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.lootbox.step4-body')}
      </Text>
    ),
  },
  {
    title: t('rules.lootbox.step5-title'),
    graphic: Lootbox5,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.lootbox.step5-body')}
      </Text>
    ),
  },
]

const getDoubleSteps = (t: TFunc): Array<Step> => [
  {
    title: t('rules.double.step1-title'),
    graphic: Double1,
    video: '/assets/videos/double_rule.mp4',
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.double.step1-body')}
      </Text>
    ),
  },
  {
    title: t('rules.double.step2-title'),
    graphic: Double2,
    content: (
      <UnorderedList userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        <ListItem mb={1}>{t('rules.double.step2-item1')}</ListItem>
        <ListItem mb={1}>{t('rules.double.step2-item2')}</ListItem>
        <ListItem mb={1}>{t('rules.double.step2-item3')}</ListItem>
      </UnorderedList>
    ),
  },
  {
    title: t('rules.double.step3-title'),
    graphic: Double3,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.double.step3-body')}
      </Text>
    ),
  },
  {
    title: t('rules.double.step4-title'),
    graphic: Double4,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.double.step4-body')}
      </Text>
    ),
  },
]

const getOpeningCompetitionSteps = (t: TFunc): Array<Step> => [
  {
    title: t('rules.opening-competition.step1-title'),
    graphic: Competition1,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.opening-competition.step1-body1')}
        <br />
        <br /> {t('rules.opening-competition.step1-body2')}
      </Text>
    ),
  },
  {
    title: t('rules.opening-competition.step2-title'),
    graphic: Competition2,
    content: (
      <>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'} mb={1}>
          {t('rules.opening-competition.step2-intro')}
        </Text>
        <UnorderedList userSelect={'none'} fontSize={'sm'} fontWeight={700} color={'white'}>
          <ListItem mb={1}>{t('rules.opening-competition.step2-item1')}</ListItem>
          <ListItem mb={1}>{t('rules.opening-competition.step2-item2')}</ListItem>
          <ListItem mb={2}>{t('rules.opening-competition.step2-item3')}</ListItem>
        </UnorderedList>
        <Text mb={1} userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          {t('rules.opening-competition.step2-nft-intro')}
        </Text>
        <UnorderedList userSelect={'none'} fontSize={'sm'} fontWeight={700} color={'white'}>
          <ListItem mb={1}>{t('rules.opening-competition.step2-nft1')}</ListItem>
          <ListItem mb={1}>{t('rules.opening-competition.step2-nft2')}</ListItem>
          <ListItem mb={2}>{t('rules.opening-competition.step2-nft3')}</ListItem>
        </UnorderedList>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          {t('rules.opening-competition.step2-learn-more')}{' '}
          <ChakraLink
            textDecor={'underline'}
            fontWeight={700}
            target="_blank"
            href={'https://injective.talis.art/collection/65d214819b0824cc8e79d0d9'}
          >
            {t('rules.opening-competition.step2-nft-link')}
          </ChakraLink>{' '}
          {t('rules.opening-competition.step2-and')}{' '}
          <ChakraLink
            textDecor={'underline'}
            fontWeight={700}
            target="_blank"
            href={'https://docs.blaze.ninja/introduction/nft'}
          >
            {t('rules.opening-competition.step2-levels-link')}
          </ChakraLink>
          .
        </Text>
      </>
    ),
  },
  {
    title: t('rules.opening-competition.step3-title'),
    graphic: Competition3,
    content: (
      <>
        <Text mb={1} userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          {t('rules.opening-competition.step3-body1-prefix')}{' '}
          <Text as={'span'} color={'white'} fontWeight={700}>
            {t('rules.opening-competition.step3-body1-amount')}
          </Text>{' '}
          {t('rules.opening-competition.step3-body1-suffix')}
        </Text>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          {t('rules.opening-competition.step3-body2-prefix')}{' '}
          <Text as={'span'} color={'white'} fontWeight={700}>
            {t('rules.opening-competition.step3-body2-count')}
          </Text>{' '}
          {t('rules.opening-competition.step3-body2-middle')}{' '}
          <Text as={'span'} color={'white'} fontWeight={700}>
            {t('rules.opening-competition.step3-body2-reward')}
          </Text>{' '}
          {t('rules.opening-competition.step3-body2-suffix')}{' '}
        </Text>
      </>
    ),
  },
  {
    title: t('rules.opening-competition.step4-title'),
    graphic: Competition4,
    content: (
      <>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'} mb={1}>
          {t('rules.opening-competition.step4-intro')}
        </Text>
        <UnorderedList userSelect={'none'} fontSize={'sm'} fontWeight={700} color={'white'}>
          <ListItem mb={1}>{t('rules.opening-competition.step4-item1')}</ListItem>
          <ListItem mb={1}>{t('rules.opening-competition.step4-item2')}</ListItem>
          <ListItem mb={1}>{t('rules.opening-competition.step4-item3')} </ListItem>
          <ListItem mb={1}>{t('rules.opening-competition.step4-item4')}</ListItem>
          <ListItem mb={1}>{t('rules.opening-competition.step4-item5')}</ListItem>
          <ListItem mb={1}>{t('rules.opening-competition.step4-item6')}</ListItem>
        </UnorderedList>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          {t('rules.opening-competition.step4-outro')}
        </Text>
      </>
    ),
  },
  {
    title: t('rules.opening-competition.step5-title'),
    graphic: Competition6,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.opening-competition.step5-body')}
      </Text>
    ),
  },
  {
    title: t('rules.opening-competition.step6-title'),
    graphic: Competition5,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.opening-competition.step6-body')}{' '}
      </Text>
    ),
    cta: (onClose) => (
      <Button onClick={() => onClose?.()} w={'full'} rounded={'2xl'}>
        {t('rules.opening-competition.step6-cta')}
      </Button>
    ),
  },
]

const getRunesCompetitionSteps = (t: TFunc): Array<Step> => [
  {
    title: t('rules.runes-competition.step1-title'),
    graphic: Competition1,
    content: (
      <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
        {t('rules.runes-competition.step1-body')}
        <br />
        <br />
        <Text as={'span'} fontWeight={700} color={'white'}>
          {t('rules.runes-competition.step1-equation')}
        </Text>
        <br />
        {t('rules.runes-competition.step1-outro')}
      </Text>
    ),
  },
  {
    title: t('rules.runes-competition.step2-title'),
    graphic: Competition4,
    content: (
      <>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'} mb={2}>
          {t('rules.runes-competition.step2-intro')}
        </Text>
        <UnorderedList userSelect={'none'} fontSize={'sm'} fontWeight={700} color={'white'}>
          <ListItem mb={1}>🥇 {t('rules.runes-competition.step2-item1')}</ListItem>
          <ListItem mb={1}>🥈 {t('rules.runes-competition.step2-item2')}</ListItem>
          <ListItem mb={1}>🥉 {t('rules.runes-competition.step2-item3')}</ListItem>
          <ListItem mb={2}>🎲 {t('rules.runes-competition.step2-item4')}</ListItem>
        </UnorderedList>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          {t('rules.runes-competition.step2-outro')}
        </Text>
      </>
    ),
  },
  {
    title: t('rules.runes-competition.step3-title'),
    graphic: Competition5,
    content: (
      <>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'} mb={2}>
          {t('rules.runes-competition.step3-body')}
        </Text>
        <Text userSelect={'none'} fontSize={'sm'} fontWeight={700} color={'white'} mb={1}>
          {t('rules.runes-competition.step3-tips-title')}
        </Text>
        <UnorderedList userSelect={'none'} fontSize={'sm'} fontWeight={500} color={'ninja.100'}>
          <ListItem mb={1}>{t('rules.runes-competition.step3-tip1')}</ListItem>
          <ListItem mb={1}>{t('rules.runes-competition.step3-tip2')}</ListItem>
          <ListItem>{t('rules.runes-competition.step3-tip3')}</ListItem>
        </UnorderedList>
      </>
    ),
    cta: (onClose) => (
      <Button onClick={() => onClose?.()} w={'full'} rounded={'2xl'}>
        {t('rules.runes-competition.step3-cta')}
      </Button>
    ),
  },
]
